// src/contexts/DataContext.js
// Centralized state: auth, students, programs, current academic period.
// Eliminates per-page re-fetching and duplicated completion analysis.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { collection, doc, getDoc, getDocs, setDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { analyzeStudentCompletion, buildStudentRecords } from '../utils/completion';

const DataContext = createContext();

export function useDataContext() {
  return useContext(DataContext);
}

const SETTINGS_DOC = ['appSettings', 'current'];

export function DataProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [rawStudents, setRawStudents] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [gradYears, setGradYears] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentSchoolYear, setCurrentSchoolYearState] = useState('');
  const [currentSemester, setCurrentSemesterState] = useState('1');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      setUser(u);
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  // Load shared academic-period settings once authenticated.
  // Falls back to the legacy per-user doc so existing deployments migrate cleanly.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const shared = await getDoc(doc(db, ...SETTINGS_DOC));
        if (cancelled) return;
        if (shared.exists()) {
          const d = shared.data();
          if (d.currentSchoolYear) setCurrentSchoolYearState(d.currentSchoolYear);
          if (d.currentSemester) setCurrentSemesterState(d.currentSemester);
          return;
        }
        const legacy = await getDoc(doc(db, 'userSettings', user.uid));
        if (cancelled) return;
        if (legacy.exists()) {
          const d = legacy.data();
          if (d.currentSchoolYear) setCurrentSchoolYearState(d.currentSchoolYear);
          if (d.currentSemester) setCurrentSemesterState(d.currentSemester);
        }
      } catch (err) {
        console.error('Error loading settings:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const persistSettings = useCallback(async (patch) => {
    try {
      await setDoc(
        doc(db, ...SETTINGS_DOC),
        { currentSchoolYear, currentSemester, ...patch },
        { merge: true }
      );
    } catch (err) {
      console.error('Error saving settings:', err);
    }
  }, [currentSchoolYear, currentSemester]);

  const setCurrentSchoolYear = useCallback((year) => {
    setCurrentSchoolYearState(year);
    persistSettings({ currentSchoolYear: year });
  }, [persistSettings]);

  const setCurrentSemester = useCallback((semester) => {
    setCurrentSemesterState(semester);
    persistSettings({ currentSemester: semester });
  }, [persistSettings]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [programSnap, studentSnap] = await Promise.all([
        getDocs(collection(db, 'programs')),
        getDocs(collection(db, 'students'))
      ]);

      const programList = programSnap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        approvalType: d.data().approvalType || 'statewide'
      }));
      setPrograms(programList);

      const built = buildStudentRecords(studentSnap);
      setRawStudents(built.students);
      setGradYears(built.gradYears);
      setSchoolYears(built.schoolYears);

      // Default to most recent school year on first load
      setCurrentSchoolYearState(prev => {
        if (prev) return prev;
        return built.schoolYears.length > 0
          ? built.schoolYears[built.schoolYears.length - 1]
          : '';
      });
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setRawStudents([]);
      setPrograms([]);
      setLoading(false);
      return;
    }
    fetchData();
  }, [user, fetchData]);

  // Pre-analyze students against every program so all pages get the same shape.
  const students = useMemo(() => {
    return rawStudents.map(student => {
      const programStatus = {};
      programs.forEach(program => {
        programStatus[program.id] = analyzeStudentCompletion(
          student,
          program,
          currentSchoolYear,
          currentSemester
        );
      });
      return { ...student, programStatus };
    });
  }, [rawStudents, programs, currentSchoolYear, currentSemester]);

  const uniqueCourseNames = useMemo(() => {
    const set = new Set();
    rawStudents.forEach(s => s.courses.forEach(c => {
      if (c.courseName) set.add(c.courseName);
    }));
    return Array.from(set).sort();
  }, [rawStudents]);

  const value = {
    user,
    authLoading,
    students,
    programs,
    uniqueCourseNames,
    schoolYears,
    gradYears,
    currentSchoolYear,
    setCurrentSchoolYear,
    currentSemester,
    setCurrentSemester,
    loading,
    refreshData: fetchData
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}
